<script setup>
import {computed, ref, watch} from "vue";
import {Checkbox, Dialog} from "primevue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Icon} from "@iconify/vue";
import {useChatStore} from "@/stores/chat.js";
import {useBreakpoint} from "@/composables/useBreakpoint.js";

const chatStore = useChatStore();
const {isXL} = useBreakpoint();

const selectedStores = ref([]);
const selectedGameServices = ref({});
const gameSearch = ref("");

const filteredGames = computed(() => {
  const search = gameSearch.value.trim().toLowerCase();

  if (!search) return chatStore.get_all_games;

  return chatStore.get_all_games.filter(game =>
      game.label.toLowerCase().includes(search)
  );
});

const visible = computed({
  get: () => chatStore.filterDrawer,
  set: value => chatStore.filterDrawer = value,
});

watch(visible, isVisible => {
  if (!isVisible) return;

  selectedStores.value = [...chatStore.filters.stores];
  selectedGameServices.value = {...(chatStore.filters.gameServices || {})};
  gameSearch.value = "";
});

function isStoreSelected(store) {
  return selectedStores.value.some(item => item.value === store.value);
}

function toggleStore(store) {
  if (isStoreSelected(store)) {
    selectedStores.value = selectedStores.value.filter(item => item.value !== store.value);
    return;
  }

  selectedStores.value.push(store);
}

function isServiceSelected(game, service) {
  return selectedGameServices.value[game.value]?.includes(service.value) || false;
}

function toggleService(game, service) {
  const gameServices = selectedGameServices.value[game.value] || [];

  if (!gameServices.includes(service.value)) {
    selectedGameServices.value[game.value] = [...gameServices, service.value];
    return;
  }

  const newServices = gameServices.filter(item => item !== service.value);

  if (newServices.length) {
    selectedGameServices.value[game.value] = newServices;
  } else {
    delete selectedGameServices.value[game.value];
  }
}

function toggleAllGameServices(game) {
  const selectedServices = selectedGameServices.value[game.value] || [];
  const allServicesSelected = chatStore.allServices.every(service => selectedServices.includes(service.value));

  if (allServicesSelected) {
    delete selectedGameServices.value[game.value];
    return;
  }

  selectedGameServices.value[game.value] = chatStore.allServices.map(service => service.value);
}

function toggleServiceForAllGames(service) {
  const allGamesSelected = chatStore.get_all_games.every(game => selectedGameServices.value[game.value]?.includes(service.value));

  chatStore.get_all_games.forEach(game => {
    const gameServices = selectedGameServices.value[game.value] || [];

    if (!allGamesSelected && !gameServices.includes(service.value)) {
      selectedGameServices.value[game.value] = [...gameServices, service.value];
      return;
    }

    if (allGamesSelected) {
      const newServices = gameServices.filter(item => item !== service.value);

      if (newServices.length) {
        selectedGameServices.value[game.value] = newServices;
      } else {
        delete selectedGameServices.value[game.value];
      }
    }
  });
}

function toggleAllGamesAndServices() {
  const allSelected = chatStore.get_all_games.every(game =>
      chatStore.allServices.every(service =>
          selectedGameServices.value[game.value]?.includes(service.value)
      )
  );

  if (allSelected) {
    selectedGameServices.value = {};
    return;
  }

  const serviceValues = chatStore.allServices.map(service => service.value);
  const allGameServices = {};

  chatStore.get_all_games.forEach(game => {
    allGameServices[game.value] = [...serviceValues];
  });

  selectedGameServices.value = allGameServices;
}

function resetFilters() {
  selectedStores.value = [];
  selectedGameServices.value = {};
}

function applyFilters() {
  chatStore.filters.stores = selectedStores.value;
  chatStore.filters.gameServices = selectedGameServices.value;
  visible.value = false;
}
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['w-full max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-3xl max-h-[calc(100vh-100px)] !px-3 !pb-3 xl:!px-4 !pb-4">
    <template #header>
      <div class="flex flex-row justify-between w-full">
        <div class="text-base py-3">
          Filters
        </div>
        <button @click="resetFilters" class="hover:underline text-xs">Reset Filters</button>
      </div>
    </template>

    <template #closebutton="{closeCallback}">
      <button type="button" class="text-xl text-slate-400 hover:text-red-500" @click="closeCallback">
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>

    <div class="flex min-h-0 flex-1 flex-col gap-4 pt-4 text-xs 2xl:text-sm">
      <div class="flex items-center justify-end gap-3">
        <div
            class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
            </svg>
          </span>
          <input v-model="gameSearch" type="text" placeholder="Enter a game"
                 class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
          <button v-if="gameSearch"
                  @click="gameSearch = ''" class="hover:text-red-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                    stroke-width="1.5"
                    d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
            </svg>
          </button>
        </div>
        <div class="inline-flex overflow-hidden rounded-md border border-slate-700">
          <button v-for="store in chatStore.allStores" :key="store.value" type="button"
                  class="border-r border-slate-700 px-3 py-2 text-xs transition-colors last:border-r-0"
                  :class="isStoreSelected(store)
              ? 'bg-slate-600 text-white'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800'"
                  @click="toggleStore(store)">
            {{ store.label }}
          </button>
        </div>
      </div>
      <DataTable
          :value="filteredGames"
          scrollable
          scroll-height="360px"
          :virtual-scroller-options="{itemSize: 32, numToleratedItems: 10}"
          striped-rows
          size="small"
          class="w-full text-xs"
          :table-style="{minWidth: '700px', tableLayout: 'fixed'}"
          data-key="value"
          :row-class="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column field="label" style="width: 200px; min-width: 200px">
          <template #header>
            <button type="button"
                    class="w-full text-left font-medium">
              Game
            </button>
          </template>
          <template #body="{data}">
            <button type="button"
                    class="w-full cursor-pointer truncate text-left font-medium hover:text-blue-400 hover:underline"
                    :title="`Select all services for ${data.label}`" @click="toggleAllGameServices(data)">
              {{ data.label }}
            </button>
          </template>
        </Column>

        <Column v-for="service in chatStore.allServices" :key="service.value" header-class="text-left"
                body-class="text-center" style="min-width: 100px">
          <template #header>
            <button type="button"
                    class="w-full text-left font-medium">
              {{ service.label }}
            </button>
          </template>
          <template #body="{data: game}">
            <Checkbox :input-id="`game-${game.value}-${service.value}`"
                      binary
                      :model-value="isServiceSelected(game, service)"
                      :aria-label="`${game.label} - ${service.label}`"
                      @update:model-value="toggleService(game, service)"
            />
          </template>
        </Column>

        <template #empty>
          <div class="py-5 text-center text-slate-400">No games found.</div>
        </template>
      </DataTable>
    </div>

    <div class="flex justify-end gap-3 border-t border-slate-700 pt-4">
      <button class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              @click="visible = false">
        Cancel
      </button>
      <button class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              @click="applyFilters">
        Apply Filters
      </button>
    </div>
  </Dialog>
</template>
